import {
  INTRANET_AI_ASK_PAGE_SIZE,
  INTRANET_AI_QUESTION_MAX,
  PermissionCode,
  intranetContentPath,
  type IntranetAiAskHistoryItem,
  type IntranetAiAskResponse,
  type IntranetAiAskSource,
} from '@gb-ticket/shared-types';
import { ChevronDownIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { type FormEvent, useId, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { KiMark } from '@/components/ki-mark';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ListPagination } from '@/components/list-pagination';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { AiLiveReply } from '@/platform/components/ai-live-reply';
import { IntranetAiAnswer } from '@/products/intranet/components/intranet-ai-answer';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import {
  useAskIntranetAi,
  useDeleteAllIntranetAiAsks,
  useDeleteIntranetAiAsk,
  useIntranetAiAgentsMine,
  useIntranetAiAskHistory,
  useIntranetAssistant,
} from '@/products/intranet/features/intranet/use-intranet-ai';

type IntranetAskProps = {
  /** Start feed: whole intranet only. KI page: agents, form card, and own history. */
  variant: 'home' | 'page';
};

/** Home: „Intranet fragen“ under search. Page `/intranet/ki`: agents, cards, history. */
export function IntranetAsk({ variant }: IntranetAskProps) {
  const copy = useVoiceCopy();
  const me = useMe();
  const assistant = useIntranetAssistant();
  const assistantOn = assistant.data?.intranetAssistant === true;
  const canUse = hasPermission(me.data, PermissionCode.INTRANET_AI_USE);
  const mine = useIntranetAiAgentsMine(variant === 'page' && assistantOn && canUse);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyScope, setHistoryScope] = useState<'agent' | 'intranet'>('agent');
  const history = useIntranetAiAskHistory(
    variant === 'page' && assistantOn && canUse,
    historyScope,
    historyPage,
    INTRANET_AI_ASK_PAGE_SIZE,
  );
  const ask = useAskIntranetAi();
  const questionId = useId();
  const agentFieldId = useId();
  const [agentId, setAgentId] = useState('');
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<IntranetAiAskResponse | null>(null);
  const [lastQuestion, setLastQuestion] = useState('');
  const [lastAgentName, setLastAgentName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [homeOpen, setHomeOpen] = useState(false);
  const homePanelId = useId();
  const homePanelOpen = homeOpen || ask.isPending;
  const agents = variant === 'page' && assistantOn && canUse ? (mine.data?.items ?? []) : [];
  const defaultAgent = agents[0];
  const pageAgentId =
    variant === 'page' && defaultAgent
      ? agents.some((agent) => agent.id === agentId)
        ? agentId
        : defaultAgent.id
      : '';
  const showAgentPicker = variant === 'page' && agents.length > 1;
  const showAgentLabel = variant === 'page' && agents.length === 1;
  const questionLabel =
    variant === 'home' ? 'Intranet fragen' : copy('intranet.ask.question-label');
  const selectedAgentName =
    variant === 'page' ? (agents.find((agent) => agent.id === pageAgentId)?.name ?? null) : null;

  if (!canUse) {
    return null;
  }

  if (variant === 'page' && assistant.data && !assistantOn) {
    return (
      <p className="text-sm text-muted-foreground">Der Intranet-Assistent ist ausgeschaltet.</p>
    );
  }

  if (!assistantOn) {
    return null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = question.trim();
    if (!text) {
      return;
    }
    setError(null);
    if (variant === 'home') {
      setHomeOpen(true);
    }
    try {
      const next = await ask.mutateAsync({
        question: text,
        ...(variant === 'page' && pageAgentId ? { agentId: pageAgentId } : {}),
      });
      setResult(next);
      setLastQuestion(text);
      setLastAgentName(selectedAgentName);
      setQuestion('');
      if (variant === 'page') {
        setHistoryPage(1);
      }
    } catch (caught) {
      setResult(null);
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Die Frage konnte nicht gestellt werden.',
      );
    }
  }

  const form = (
    <form className="space-y-4" onSubmit={(event) => void submit(event)}>
      {showAgentLabel && defaultAgent ? (
        <div>
          <p className="text-sm font-medium">Agent</p>
          <p className="mt-1 text-sm text-muted-foreground">{defaultAgent.name}</p>
        </div>
      ) : null}
      {showAgentPicker ? (
        <div>
          <Label htmlFor={agentFieldId}>Agent</Label>
          <Select
            value={pageAgentId || undefined}
            disabled={ask.isPending}
            onValueChange={setAgentId}
          >
            <SelectTrigger id={agentFieldId} className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {variant === 'page' && !mine.isPending && agents.length === 0 ? (
        <p role="alert" className="text-sm text-destructive">
          {copy('intranet.ask.no-agent')}
        </p>
      ) : null}
      <div className="space-y-2">
        <Label htmlFor={questionId}>{questionLabel}</Label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <Textarea
            id={questionId}
            className="min-h-24 flex-1 bg-background sm:min-h-20"
            value={question}
            maxLength={INTRANET_AI_QUESTION_MAX}
            placeholder="Frage zu den Dateien dieses Agenten"
            disabled={ask.isPending}
            onChange={(event) => setQuestion(event.target.value)}
          />
          <Button
            type="submit"
            className="w-full shrink-0 sm:w-auto"
            disabled={
              ask.isPending || question.trim().length === 0 || (variant === 'page' && !pageAgentId)
            }
          >
            {ask.isPending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
            Fragen
          </Button>
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </form>
  );

  if (variant === 'home') {
    return (
      <div className="space-y-3">
        <article className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <button
            type="button"
            className={cn(
              'flex min-h-11 w-full items-center gap-2 px-3 py-2.5 text-left sm:px-4',
              'hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none focus-visible:ring-inset',
              homePanelOpen && 'border-b',
            )}
            aria-expanded={homePanelOpen}
            aria-controls={homePanelId}
            onClick={() => setHomeOpen((value) => !value)}
          >
            <KiMark className="size-4 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-primary">Intranet fragen</span>
              {!homePanelOpen ? (
                <span className="block truncate text-xs text-muted-foreground">
                  Frage zu veröffentlichten Beiträgen stellen
                </span>
              ) : null}
            </span>
            <ChevronDownIcon
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform',
                homePanelOpen && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
          {homePanelOpen ? (
            <form
              id={homePanelId}
              className="space-y-2.5 px-3 py-3 sm:px-4"
              onSubmit={(event) => void submit(event)}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                  <Label htmlFor={questionId} className="sr-only">
                    Intranet fragen
                  </Label>
                  <Textarea
                    id={questionId}
                    className="min-h-14 bg-background md:min-h-16"
                    rows={2}
                    value={question}
                    maxLength={INTRANET_AI_QUESTION_MAX}
                    placeholder={copy('intranet.ask.question-placeholder')}
                    disabled={ask.isPending}
                    onChange={(event) => setQuestion(event.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full shrink-0 sm:min-h-10 sm:w-auto"
                  disabled={ask.isPending || question.trim().length === 0}
                >
                  {ask.isPending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
                  Fragen
                </Button>
              </div>
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
            </form>
          ) : null}
        </article>
        {result ? (
          <AskResultCard
            question={lastQuestion}
            answer={result.answer}
            sources={result.sources}
            agentName={null}
            showAgent={false}
          />
        ) : null}
        {ask.isPending ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-primary">{question.trim()}</p>
            <AiLiveReply active text="" />
          </div>
        ) : null}
      </div>
    );
  }

  const historyItems = history.data?.items ?? [];
  const historyTotal = history.data?.total ?? 0;
  const newestMatchesResult =
    result !== null &&
    historyPage === 1 &&
    historyItems[0]?.question === lastQuestion &&
    historyItems[0]?.answer === result.answer;
  const showPendingResult = result !== null && !newestMatchesResult;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <article className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="space-y-1 border-b px-4 py-3 sm:px-5">
          <h2 className="flex items-center gap-2 text-base font-semibold text-primary sm:text-lg">
            <KiMark className="size-5" />
            Neue Frage
          </h2>
          <p className="text-sm text-muted-foreground">
            Jede Frage steht für sich. Dieser Agent sieht nur seine Dateien, nicht das Intranet und
            nicht den Helpdesk.
          </p>
        </div>
        <div className="space-y-3 px-4 py-3 sm:px-5">
          {form}
          {ask.isPending ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-primary">{question.trim()}</p>
              <AiLiveReply active text="" />
            </div>
          ) : null}
        </div>
      </article>

      <section className="space-y-3" aria-labelledby="intranet-ai-history-heading">
        <Tabs
          value={historyScope}
          onValueChange={(value) => {
            setHistoryScope(value === 'intranet' ? 'intranet' : 'agent');
            setHistoryPage(1);
            setResult(null);
          }}
        >
          <TabsList>
            <TabsTrigger value="agent">Agenten</TabsTrigger>
            <TabsTrigger value="intranet">Intranet</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h2 id="intranet-ai-history-heading" className="text-lg font-semibold tracking-tight">
              {historyScope === 'agent' ? copy('intranet.ask.history-heading') : 'Intranet-Fragen'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {historyScope === 'agent'
                ? copy('intranet.ask.history-hint')
                : 'Fragen von „Intranet fragen“ auf der Startseite.'}{' '}
              Die letzten 30 bleiben gespeichert.
              {historyTotal > 0 ? ` (${historyTotal} gesamt)` : null}
            </p>
          </div>
          {historyTotal > 0 || showPendingResult ? (
            <DeleteAllHistoryButton
              scope={historyScope}
              onDeleted={() => {
                setResult(null);
                setHistoryPage(1);
              }}
            />
          ) : null}
        </div>
        {history.isPending && !showPendingResult ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Verlauf wird geladen …
          </p>
        ) : null}
        {history.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Der Verlauf konnte nicht geladen werden.
          </p>
        ) : null}
        {!history.isPending && !history.isError && historyTotal === 0 && !showPendingResult ? (
          <article className="rounded-xl border bg-card px-4 py-8 text-sm text-muted-foreground shadow-sm">
            {historyScope === 'agent'
              ? copy('intranet.ask.empty')
              : copy('intranet.ask.home-empty')}
          </article>
        ) : null}
        <ul className="space-y-3">
          {showPendingResult && result ? (
            <li>
              <AskResultCard
                question={lastQuestion}
                answer={result.answer}
                sources={result.sources}
                agentName={lastAgentName}
              />
            </li>
          ) : null}
          {historyItems.map((item) => (
            <li key={item.id}>
              <AskHistoryCard item={item} />
            </li>
          ))}
        </ul>
        {history.data ? (
          <ListPagination
            page={history.data.page}
            pageSize={history.data.pageSize}
            total={history.data.total}
            onPageChange={setHistoryPage}
          />
        ) : null}
      </section>
    </div>
  );
}

function DeleteAllHistoryButton({
  scope,
  onDeleted,
}: {
  scope: 'agent' | 'intranet';
  onDeleted: () => void;
}) {
  const copy = useVoiceCopy();
  const removeAll = useDeleteAllIntranetAiAsks();
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function confirmDelete() {
    try {
      const result = await removeAll.mutateAsync(scope);
      setConfirmOpen(false);
      onDeleted();
      toast.success(
        result.deleted === 1 ? 'Eine Frage gelöscht.' : `${result.deleted} Fragen gelöscht.`,
      );
    } catch (caught) {
      toast.error(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Der Verlauf konnte nicht gelöscht werden.',
      );
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-destructive hover:text-destructive"
        disabled={removeAll.isPending}
        onClick={() => setConfirmOpen(true)}
      >
        {removeAll.isPending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
        Alle löschen
      </Button>
      <ConfirmDeleteDialog
        open={confirmOpen}
        title={scope === 'agent' ? 'Agenten-Verlauf löschen?' : 'Intranet-Verlauf löschen?'}
        description={
          scope === 'agent'
            ? copy('intranet.ask.clear-all')
            : 'Alle gespeicherten Intranet-Fragen werden gelöscht. Agenten-Fragen bleiben.'
        }
        pending={removeAll.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
}

function AskHistoryCard({ item }: { item: IntranetAiAskHistoryItem }) {
  const copy = useVoiceCopy();
  const remove = useDeleteIntranetAiAsk();
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function confirmDelete() {
    try {
      await remove.mutateAsync(item.id);
      setConfirmOpen(false);
      toast.success('Frage gelöscht.');
    } catch (caught) {
      toast.error(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Die Frage konnte nicht gelöscht werden.',
      );
    }
  }

  return (
    <>
      <AskResultCard
        question={item.question}
        answer={item.answer}
        sources={item.sources}
        agentName={item.agentName}
        createdAt={item.createdAt}
        onDelete={() => setConfirmOpen(true)}
        deletePending={remove.isPending}
      />
      <ConfirmDeleteDialog
        open={confirmOpen}
        title="Frage löschen?"
        description={copy('intranet.ask.clear-one')}
        pending={remove.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
}

function AskResultCard({
  question,
  answer,
  sources,
  agentName,
  createdAt,
  showAgent = true,
  onDelete,
  deletePending = false,
}: {
  question: string;
  answer: string;
  sources: IntranetAiAskSource[];
  agentName: string | null;
  createdAt?: string;
  showAgent?: boolean;
  onDelete?: () => void;
  deletePending?: boolean;
}) {
  const showMeta = Boolean(createdAt) || showAgent;
  return (
    <article className="rounded-xl border bg-card shadow-sm">
      <div className="space-y-2 border-b px-4 py-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 font-medium text-primary">
                <KiMark className="size-3.5" />
                KI-Antwort
              </span>
              {showMeta && createdAt ? (
                <time dateTime={createdAt}>{formatDateTime(createdAt)}</time>
              ) : null}
              {showAgent ? <span>{agentName ?? 'Gesamtes Intranet'}</span> : null}
            </div>
            <h3 className="text-base leading-snug font-semibold break-words text-primary sm:text-lg">
              {question}
            </h3>
          </div>
          {onDelete ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9 shrink-0 text-muted-foreground hover:text-destructive"
              aria-label="Frage löschen"
              disabled={deletePending}
              onClick={onDelete}
            >
              {deletePending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trash2Icon className="size-4" aria-hidden />
              )}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="space-y-3 px-4 py-3">
        <div className="rounded-lg border bg-muted/40 px-3 py-3">
          <IntranetAiAnswer text={answer} />
        </div>
        {sources.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {sources.length === 1 ? 'Quelle' : 'Quellen'}
            </p>
            <ul className="space-y-1">
              {sources.map((source) => (
                <li key={`${source.kind}-${source.slug}-${source.href ?? ''}`}>
                  <Link
                    to={source.href ?? intranetContentPath(source)}
                    className="text-sm text-primary underline-offset-2 hover:underline"
                  >
                    {source.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </article>
  );
}
